import { cn, getBackgroundClass } from '@workspace/lib';
import { ResolvedLink } from '@workspace/ui';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardMedia,
  CardTitle,
  cardVariants,
  Icon,
  Picture,
  Typography,
} from '@workspace/ui/common';
import { cardGridVariants } from '@workspace/blocks/CardGrid/cardGrid.variants';

import type { CardGridProps, RenderableCardGridItem } from './card-grid.types';

export function CardGrid({ block }: CardGridProps) {
  if (!block || block.isActive === false) return null;

  const cards = (block.cards ?? [])
    .filter((card): card is RenderableCardGridItem => Boolean(card?.title && (card.image || card.bynderImage)))
    .slice(0, 2);
  if (!cards.length) return null;

  const backgroundClass = getBackgroundClass(block.backgroundColor ?? 'bg-white');
  const { section, container, grid } = cardGridVariants();
  const { image: defaultCardImage } = cardVariants();

  return (
    <section className={cn('w-full', section(), backgroundClass)}>
      <div className={cn('porta-container', container())}>
        <div className={cn('grid grid-cols-1', grid())}>
          {cards.map((card, index) => {
            const image = card.image ?? card.bynderImage;
            const imageAlt = card.image?.alt ?? card.bynderImage?.alt ?? card.title;
            const {
              card: cardSlot,
              imageWrapper,
              image: imageSlot,
              content,
              titleRow,
              title,
              arrow,
              description,
            } = cardGridVariants({ position: index === 0 ? 'first' : 'last' });

            return (
              <ResolvedLink
                key={card._key ?? index}
                link={card.link}
                className="group block min-w-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-300"
              >
                <Card className={cardSlot()}>
                  <CardMedia className={imageWrapper()}>
                    <Picture
                      image={image ?? undefined}
                      alt={imageAlt}
                      desktopWidth={708}
                      desktopHeight={708}
                      mobileWidth={374}
                      mobileHeight={374}
                      imgClassName={cn(
                        'transition-transform duration-300 ease-in-out group-hover:scale-[1.03] motion-reduce:transform-none',
                        defaultCardImage(),
                        imageSlot(),
                      )}
                    />
                  </CardMedia>

                  <CardContent className={content()}>
                    <CardHeader className={titleRow()}>
                      <CardTitle>
                        <Typography variant="h3" styleVariant="h3" className={title()}>
                          {card.title}
                        </Typography>
                      </CardTitle>
                      <Icon
                        name="arrowRightIcon"
                        size={40}
                        className={cn(
                          'transition-transform duration-300 ease-in-out group-hover:translate-x-0.5 motion-reduce:transform-none',
                          arrow(),
                        )}
                      />
                    </CardHeader>

                    {card.description && (
                      <CardDescription>
                        <Typography variant="bodyMd" className={description()}>
                          {card.description}
                        </Typography>
                      </CardDescription>
                    )}
                  </CardContent>
                </Card>
              </ResolvedLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}
